import { useEffect, useState, type FormEvent } from 'react';
import { Button, Drawer, Stack, Text, Textarea } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

export interface ReportProblemSheetProps {
  opened: boolean;
  onClose: () => void;
  stepLabel: string;
  /** The note already reported on this step (to edit it). */
  initialNote?: string | null;
  onSubmit: (note: string) => void;
}

/**
 * "Report a problem" on a step: a bottom sheet with one required note. The step is marked Problem
 * and the procedure owners are told. Saved on the phone first, like every tick.
 */
export function ReportProblemSheet({ opened, onClose, stepLabel, initialNote, onSubmit }: ReportProblemSheetProps) {
  const [note, setNote] = useState(initialNote ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setNote(initialNote ?? '');
      setError(null);
    }
  }, [opened, initialNote]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = note.trim();
    if (text.length < 3) {
      setError('Please say what is wrong, so the team can fix it.');
      return;
    }
    onSubmit(text);
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      size="auto"
      title="Report a problem"
      classNames={{ content: 'proc-sheet', body: 'proc-sheet-body', title: 'proc-sheet-title' }}
      transitionProps={{ transition: 'slide-up', duration: 160 }}
    >
      <form onSubmit={submit}>
        <Stack gap="md" pb="md">
          <Text c="dimmed">
            {stepLabel}. Tell us what's wrong. The people who look after this procedure will see your note.
          </Text>
          <Textarea
            label="What's wrong?"
            placeholder="For example: the red switch is missing its cover"
            value={note}
            onChange={(e) => {
              setNote(e.currentTarget.value);
              if (error) setError(null);
            }}
            error={error}
            autosize
            minRows={3}
            maxRows={8}
            maxLength={2000}
            required
            data-autofocus
          />
          <Button type="submit" color="red" size="lg" fullWidth leftSection={<IconAlertTriangle size={20} aria-hidden="true" />}>
            Report problem
          </Button>
          <Button variant="subtle" color="gray" fullWidth onClick={onClose}>Cancel</Button>
        </Stack>
      </form>
    </Drawer>
  );
}
